if (!customElements.get('sprplx-collection-carousel')) {
  customElements.define('sprplx-collection-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.viewport = this.querySelector('[data-viewport]');
      this.track = this.querySelector('[data-track]');
      this.group = this.querySelector('[data-original]');
      if (!this.group?.children.length) return;
      this.position = 0;
      this.cycle = 0;
      this.hovered = false;
      this.focused = false;
      this.drag = null;
      this.suppressClick = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const on = (target, event, fn, options = {}) => target.addEventListener(event, fn, {...options, signal});
      on(this.viewport, 'pointerenter', e => { if (e.pointerType === 'mouse') this.hovered = true; });
      on(this.viewport, 'pointerleave', () => { this.hovered = false; });
      on(this, 'focusin', e => {
        this.focused = e.target.matches(':focus-visible');
        if (!this.focused) return;
        this.viewport.scrollLeft = 0;
        const card = e.target.closest('[data-card]');
        if (!card || !this.cycle) return;
        const rect = card.getBoundingClientRect(), bounds = this.viewport.getBoundingClientRect();
        if (rect.left < bounds.left + 8) this.position += rect.left - bounds.left - 8;
        else if (rect.right > bounds.right - 8) this.position += rect.right - bounds.right + 8;
        this.paint();
      });
      on(this, 'focusout', () => queueMicrotask(() => {
        if (!this.isConnected) return;
        this.focused = this.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
        if (!this.focused) { this.wrap(); this.paint(); }
      }));
      on(this.viewport, 'dragstart', e => e.preventDefault());
      on(this.viewport, 'pointerdown', e => {
        if (!e.isPrimary || e.button !== 0) return;
        this.drag = {id:e.pointerId,x:e.clientX,y:e.clientY,position:this.position,moved:false};
        this.suppressClick = false;
      });
      on(this.viewport, 'pointermove', e => {
        if (!this.drag || e.pointerId !== this.drag.id) return;
        const dx = e.clientX - this.drag.x, dy = e.clientY - this.drag.y;
        if (!this.drag.moved && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { this.drag = null; return; }
        if (!this.drag.moved && Math.abs(dx) < 6) return;
        this.drag.moved = true;
        if (!this.viewport.hasPointerCapture(e.pointerId)) this.viewport.setPointerCapture(e.pointerId);
        this.position = this.drag.position - dx;
        this.wrap();
        this.paint();
        e.preventDefault();
      }, {passive:false});
      const end = e => {
        if (!this.drag || e.pointerId !== this.drag.id) return;
        this.suppressClick = this.drag.moved;
        this.drag = null;
        if (this.viewport.hasPointerCapture(e.pointerId)) this.viewport.releasePointerCapture(e.pointerId);
      };
      on(window, 'pointerup', end);
      on(window, 'pointercancel', end);
      on(this.viewport, 'click', e => {
        if (this.suppressClick) { e.preventDefault(); e.stopImmediatePropagation(); this.suppressClick = false; }
      }, {capture:true});
      on(window, 'blur', () => { this.drag = null; this.lastTime = null; });
      on(document, 'visibilitychange', () => { this.lastTime = null; });
      on(this.reduced, 'change', () => { this.lastTime = null; });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.lastTime = null; });
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => this.build());
      this.resize.observe(this.viewport);
      this.resize.observe(this.group);
      this.build();
      this.frame = time => {
        if (signal.aborted) return;
        const dt = this.lastTime == null ? 0 : Math.min((time - this.lastTime) / 1000, .05);
        this.lastTime = time;
        if (this.visible && !document.hidden && !this.reduced.matches && !this.hovered && !this.focused && !this.drag) {
          this.position += dt * (Number(this.dataset.speed) || 40) * (this.dataset.direction === 'right' ? -1 : 1);
          this.wrap();
          this.paint();
        }
        this.raf = requestAnimationFrame(this.frame);
      };
      this.raf = requestAnimationFrame(this.frame);
    }
    build() {
      const width = this.group.getBoundingClientRect().width;
      if (!width) return;
      const phase = this.cycle ? ((this.position - this.cycle) % this.cycle + this.cycle) % this.cycle / this.cycle : 0;
      this.track.querySelectorAll('[data-copy]').forEach(el => el.remove());
      this.cycle = width;
      const count = Math.max(3, Math.ceil(this.viewport.clientWidth / width) + 3);
      const copy = () => {
        const el = this.group.cloneNode(true);
        el.removeAttribute('data-original');
        el.setAttribute('data-copy','');
        el.setAttribute('aria-hidden','true');
        for (const node of el.querySelectorAll('*')) {
          node.removeAttribute('id');
          node.removeAttribute('data-shopify-editor-block');
          if (node.matches('a,button,[tabindex]')) node.tabIndex = -1;
        }
        return el;
      };
      this.track.prepend(copy());
      for (let i = 2; i < count; i++) this.track.append(copy());
      this.position = width * (1 + phase);
      this.paint();
    }
    wrap() {
      if (this.cycle) this.position = this.cycle + ((this.position - this.cycle) % this.cycle + this.cycle) % this.cycle;
    }
    paint() { this.track.style.transform = `translate3d(${-this.position}px,0,0)`; }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.raf);
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.track?.querySelectorAll('[data-copy]').forEach(el => el.remove());
      if (this.track) this.track.style.transform = '';
      this.lastTime = null;
    }
  });
}
